import { api } from '../lib/api';
import type { Tag } from '../lib/types';

/**
 * Tags, and putting them on recordings. The list is what the picker
 * searches; adding by name makes the tag on the way in when nothing
 * matches, which is one request instead of two.
 */

export interface TagWithCount extends Tag {
  videoCount: number;
}

export interface TagList {
  tags: TagWithCount[];
  /** The team whose tags are on offer, and what you may do with them; null with no shared library. */
  team: TagTeam | null;
}

export interface TagTeam {
  id: string;
  name: string;
  /** The team lets plain members make new team tags. */
  membersCreateTags: boolean;
  /** You may make a new team tag. */
  canCreate: boolean;
  /** You may rename and delete team tags, and change the setting. */
  canManage: boolean;
}

export type TagChoice = { tagId: string } | { name: string; scope: Tag['scope'] };

export function fetchTags(q = ''): Promise<TagList> {
  const search = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  return api<TagList>(`/v1/tags${search}`);
}

export function createTag(name: string, scope: Tag['scope']): Promise<TagWithCount> {
  return api<TagWithCount>('/v1/tags', { method: 'POST', body: { name, scope } });
}

export function renameTag(id: string, name: string): Promise<TagWithCount> {
  return api<TagWithCount>(`/v1/tags/${id}`, { method: 'PATCH', body: { name } });
}

export function deleteTag(id: string): Promise<void> {
  return api<void>(`/v1/tags/${id}`, { method: 'DELETE' });
}

export function updateTagSettings(body: { membersCreateTags: boolean }): Promise<{ membersCreateTags: boolean }> {
  return api<{ membersCreateTags: boolean }>('/v1/tags/settings', { method: 'PATCH', body });
}

export function fetchVideoTags(videoId: string): Promise<{ tags: TagWithCount[] }> {
  return api<{ tags: TagWithCount[] }>(`/v1/videos/${videoId}/tags`);
}

export function addVideoTag(videoId: string, choice: TagChoice): Promise<{ tags: TagWithCount[] }> {
  return api<{ tags: TagWithCount[] }>(`/v1/videos/${videoId}/tags`, { method: 'POST', body: choice });
}

export function removeVideoTag(videoId: string, tagId: string): Promise<{ tags: TagWithCount[] }> {
  return api<{ tags: TagWithCount[] }>(`/v1/videos/${videoId}/tags/${tagId}`, { method: 'DELETE' });
}
